import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { tokens } from '../../theme';

const useStyles = makeStyles({
    toggle: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontWeight: 600,
        color: tokens.blue600,
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
        '&:focus-visible': { outline: `2px solid ${tokens.blue500}`, outlineOffset: 2, borderRadius: 2 },
    },
    clip: { position: 'relative', overflow: 'hidden' },
    fade: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 96,
        background: `linear-gradient(to bottom, rgba(255,255,255,0), ${tokens.white})`,
        pointerEvents: 'none',
    },
    blockToggle: { marginTop: 12 },
});

const TEXT_LIMIT = 300;
const LINE_LIMIT = 5;

/** Shortens text to about TEXT_LIMIT characters or LINE_LIMIT lines, cutting at a word boundary. */
export function shorten(text) {
    const lines = text.split('\n');
    let preview = lines.length > LINE_LIMIT ? lines.slice(0, LINE_LIMIT).join('\n') : text;
    if (preview.length > TEXT_LIMIT) {
        const cut = preview.lastIndexOf(' ', TEXT_LIMIT);
        preview = preview.slice(0, cut > TEXT_LIMIT / 2 ? cut : TEXT_LIMIT);
    }
    return preview === text ? null : preview.trimEnd();
}

/** Plain text (e.g. a visitor's comment) with a "Read more" link when it is long. */
export function ExpandableText({ text, className }) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const preview = shorten(text);

    return (
        <p className={className}>
            {expanded || !preview ? text : `${preview}… `}
            {preview && (
                <>
                    {expanded && ' '}
                    <button type="button" className={classes.toggle} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
                        {expanded ? 'Show less' : 'Read more'}
                    </button>
                </>
            )}
        </p>
    );
}

/** Rich content limited to maxHeight with a fade and a "Read more" button; shows everything if it fits. */
export function CollapsibleContent({ children, maxHeight = 360, label = 'Read more' }) {
    const classes = useStyles();
    const ref = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        if (ref.current) setOverflows(ref.current.scrollHeight > maxHeight + 40);
    }, [children, maxHeight]);

    // Images and fonts can change the height after the first render.
    useEffect(() => {
        const node = ref.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => setOverflows(node.scrollHeight > maxHeight + 40));
        Array.from(node.children).forEach((child) => observer.observe(child));
        return () => observer.disconnect();
    }, [children, maxHeight]);

    const collapsed = overflows && !expanded;

    return (
        <div>
            <div ref={ref} className={classes.clip} style={collapsed ? { maxHeight } : undefined}>
                {children}
                {collapsed && <div className={classes.fade} />}
            </div>
            {overflows && (
                <div className={classes.blockToggle}>
                    <button type="button" className={classes.toggle} aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
                        {expanded ? 'Show less' : label}
                        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </button>
                </div>
            )}
        </div>
    );
}
